import { useCallback, useEffect } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import { useCampaigns } from '@/modules/campaigns/hooks/useCampaigns';
import { selectDraftAutonomousActions } from '@/modules/campaigns/redux/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';

import { PaymentRecoveryMessages } from '../payment-recovery/Messages';
import { useBuildDebtCampaignLogic } from '../payment-recovery/useBuildDebtCampaignLogic';

/**
 * Step 2b sub-page: Channel message configuration for NBA campaigns
 * Wraps existing PaymentRecoveryMessages (multi-channel) page with isNba flag.
 * Route: /campaigns/:id/messaging/configure
 *
 * Calls useBuildDebtCampaignLogic to ensure sequential senders are built in Redux
 * (needed on direct navigation / page refresh).
 */
export const MessagingNBAConfig = () => {
  const navigate = useNavigate();
  const slug = useSelector(selectAccountSlug);
  // Route param is the source of truth: the session campaignId is set by
  // CampaignRouteWrapper in an effect AFTER this page first renders, so it
  // can briefly hold a previous campaign's id on fast campaign switches.
  const { campaignId } = useParams<{ campaignId: string }>();
  const draftAutonomousActions = useSelector(selectDraftAutonomousActions);
  const { campaign, getStatus } = useCampaigns(campaignId);

  // Trigger the sender-building effect so draftPaymentRecoverySenders
  // is populated on refresh (PaymentRecoveryMessages depends on it).
  useBuildDebtCampaignLogic();

  // This page is NBA-only: without nba_config the sender-building effect
  // never runs and the page shows an infinite skeleton. Redirect back.
  // Skip when draft autonomous actions is on (mirrors UnifiedMessaging's
  // routing condition) so a mid-creation NBA campaign isn't bounced.
  useEffect(() => {
    if (getStatus === 'success' && campaign?.nba_config == null && !draftAutonomousActions) {
      navigate(`/${slug}/campaigns/${campaignId}/messaging`, { replace: true });
    }
  }, [getStatus, campaign?.nba_config, draftAutonomousActions, navigate, slug, campaignId]);

  const handleBack = useCallback(() => {
    navigate(`/${slug}/campaigns/${campaignId}/messaging`);
  }, [navigate, slug, campaignId]);

  return <PaymentRecoveryMessages onBack={handleBack} isNba />;
};
